import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GeneratedMockup, mockupService } from '../services/mockupService';
import { downloadService } from '../services/downloadService';
import { libraryService } from '../services/libraryService';
import { useAuth } from '../contexts/AuthContext';
import { Save, Check } from 'lucide-react';

const Download: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [mockup, setMockup] = useState<GeneratedMockup | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'png' | 'jpg'>('png');
  const [selectedResolution, setSelectedResolution] = useState<'web' | 'print'>('web');
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [backgroundRemovedMockup, setBackgroundRemovedMockup] = useState<GeneratedMockup | null>(null);

  useEffect(() => {
    const mockupId = sessionStorage.getItem('generatedMockupId');
    if (!mockupId) {
      setError('No mockup ID found. Please generate a mockup first.');
      setIsLoading(false);
      navigate('/dashboard/create');
      return;
    }

    const fetchMockup = async () => {
      try {
        const mockupInfo = await mockupService.getMockupInfo(mockupId);
        setMockup(mockupInfo);
      } catch (err) {
        setError('Failed to load your mockup. It may have expired or an error occurred.');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMockup();
  }, [navigate]);

  const handleDownload = async (mockupId?: string) => {
    if (!mockup) return;
    
    setIsDownloading(true);
    try {
      if (mockupId && backgroundRemovedMockup && mockupId === backgroundRemovedMockup.id) {
        // Download the background-removed mockup
        await downloadService.downloadMockup(backgroundRemovedMockup.id, backgroundRemovedMockup.filename, 'png', selectedResolution);
      } else {
        // Download the original mockup
        await downloadService.downloadMockup(mockup.id, mockup.filename, selectedFormat, selectedResolution);
      }
    } catch (error: any) {
      alert(error.message || 'Download failed. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCreateAnother = () => {
    // Clear all session data and start fresh
    sessionStorage.clear();
    navigate('/dashboard/mockups');
  };

  const handleModifyAndRegenerate = () => {
    // Navigate back to customize page to modify options and regenerate
    navigate('/dashboard/customize');
  };

  const handleSaveToLibrary = async () => {
    if (!mockup || !user) return;
    
    setIsSaving(true);
    try {
      // Fetch the mockup image as blob
      const response = await fetch(`http://localhost:5001${mockup.url}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch mockup: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      
      // Validate blob
      if (!blob || blob.size === 0) {
        throw new Error('Invalid mockup file');
      }
      
      console.log('Fetched blob:', blob.type, blob.size);
      
      // Get template name from session
      const templateName = sessionStorage.getItem('selectedTemplate') || mockup.template;
      
      // Get settings from mockup
      const settings = (mockup as any).settings || {};
      
      // Save to library
      const result = await libraryService.saveMockup({
        name: `${templateName.replace(/-/g, ' ')} - ${new Date().toLocaleDateString()}`,
        templateId: mockup.template,
        templateName: templateName,
        mockupFile: blob,
        settings: settings,
        width: 1600, // Default dimensions
        height: 1200
      });
      
      if (result.mockup) {
        setIsSaved(true);
        console.log('Mockup saved to library!');
      } else {
        console.error('Save error:', result.error);
        alert(`Failed to save: ${result.error}`);
      }
    } catch (error: any) {
      console.error('Error saving to library:', error);
      alert(error.message || 'Failed to save mockup to library');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveBackground = async () => {
    if (!mockup) return;
    
    setIsRemovingBackground(true);
    try {
      const response = await mockupService.removeBackground(mockup.id);
      if (response) {
        setBackgroundRemovedMockup(response);
        console.log('Background removed successfully!');
      }
    } catch (error: any) {
      console.error('Error removing background:', error);
      alert(error.message || 'Failed to remove background');
    } finally {
      setIsRemovingBackground(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          Your Mockups Are Ready!
        </h1>
        <p className="text-lg text-gray-600">
          Download your professional mockups below
        </p>
      </div>

      {/* Download Options */}
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Download Options</h2>
        
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              File Format
            </label>
            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedFormat('png')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedFormat === 'png'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                PNG
              </button>
              <button
                onClick={() => setSelectedFormat('jpg')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedFormat === 'jpg'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                JPG
              </button>
            </div>
          </div>

          {/* Resolution Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Resolution
            </label>
            <div className="flex space-x-3">
              <button
                onClick={() => setSelectedResolution('web')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedResolution === 'web'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Web (1200px)
              </button>
              <button
                onClick={() => setSelectedResolution('print')}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedResolution === 'print'
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Print (3000px)
              </button>
            </div>
          </div>
        </div>

        <button
          onClick={() => handleDownload()}
          disabled={isDownloading}
          className="w-full px-6 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors disabled:bg-gray-400"
        >
          {isDownloading ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Downloading...
            </span>
          ) : (
            `Download All Mockups (${selectedFormat.toUpperCase()} - ${selectedResolution === 'web' ? '1200px' : '3000px'})`
          )}
        </button>
      </div>

      {/* Individual Mockups */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Generated Mockup</h2>
        
        {isLoading && (
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your mockup...</p>
          </div>
        )}

        {error && (
          <div className="text-center p-8 bg-red-50 border border-red-200 rounded-lg">
            <h3 className="text-red-800 font-semibold">An Error Occurred</h3>
            <p className="text-red-700 mt-1">{error}</p>
          </div>
        )}

        {mockup && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Original Mockup */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                <img 
                  src={`http://localhost:5001/generated/${mockup.filename}`} 
                  alt="Generated Mockup"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-2">Original Mockup</h3>
                <button
                  onClick={() => handleDownload(mockup.id)}
                  className="w-full px-4 py-2 border border-primary-600 text-primary-600 font-medium rounded-lg hover:bg-primary-50 transition-colors mb-2"
                >
                  Download
                </button>
                {!backgroundRemovedMockup && (
                  <button
                    onClick={handleRemoveBackground}
                    disabled={isRemovingBackground}
                    className="w-full px-4 py-2 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:bg-gray-400 flex items-center justify-center gap-2"
                  >
                    {isRemovingBackground ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Removing Background...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Remove Background
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Background Removed Version */}
            {backgroundRemovedMockup && (
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="aspect-square bg-gray-100 flex items-center justify-center relative">
                  {/* Checkered background to show transparency */}
                  <div className="absolute inset-0 opacity-10" style={{
                    backgroundImage: 'linear-gradient(45deg, #000 25%, transparent 25%), linear-gradient(-45deg, #000 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #000 75%), linear-gradient(-45deg, transparent 75%, #000 75%)',
                    backgroundSize: '20px 20px',
                    backgroundPosition: '0 0, 0 10px, 10px -10px, -10px 0px'
                  }}></div>
                  <img 
                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:5001'}/generated/${backgroundRemovedMockup.filename}`} 
                    alt="Background Removed Mockup"
                    className="w-full h-full object-contain relative z-10"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-1">Background Removed</h3>
                  <p className="text-sm text-gray-500 mb-2">Transparent PNG</p>
                  <button
                    onClick={() => handleDownload(backgroundRemovedMockup.id)}
                    className="w-full px-4 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Download Transparent
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Save to Library */}
      {user && !isSaved && (
        <div className="mb-6 text-center">
          <button
            onClick={handleSaveToLibrary}
            disabled={isSaving}
            className="px-6 py-3 bg-brand-purple text-white font-semibold rounded-lg hover:bg-brand-purple/90 transition-colors flex items-center justify-center gap-2 mx-auto disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Save to Library
              </>
            )}
          </button>
        </div>
      )}
      
      {isSaved && (
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-800 rounded-lg">
            <Check className="w-5 h-5" />
            Saved to Library
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row justify-center gap-4">
        <button
          onClick={handleModifyAndRegenerate}
          className="px-8 py-3 bg-primary-600 text-white font-semibold rounded-lg hover:bg-primary-700 transition-colors flex items-center justify-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
          Modify & Regenerate
        </button>
        <button
          onClick={handleCreateAnother}
          className="px-8 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors"
        >
          Create Another Mockup
        </button>
        <button
          onClick={() => navigate('/dashboard/library')}
          className="px-8 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
        >
          Go to Library
        </button>
      </div>

      {/* Tips Section */}
      <div className="mt-12 bg-primary-50 rounded-2xl p-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Pro Tips</h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start">
            <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Use Remove Background feature for transparent PNG mockups
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Choose print resolution for high-quality marketing materials
          </li>
          <li className="flex items-start">
            <svg className="w-5 h-5 text-primary-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Web resolution is perfect for social media and websites
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Download;
